import { useState } from "react";
import Switch from "../../shared/Switch";
import { useQuery, useMutation } from "react-query";
import { getModules } from "../../sdk/modules/modules";
import { addUserPermissions, getUser } from "../../sdk/users/users";
import { useParams } from "react-router-dom";
import { useError } from "../../context/ErrorProvider";
import { Circle, Ellipsis, Mail } from "lucide-react";
import AddPermissions from "../../components/add-user-permissions/AddPermissions";
import AddPermissionsSuccess from "../../components/add-user-permissions/AddPermissionsSuccess";

const EditUserPermissions = () => {
  const [permissionsState, setPermissionsState] = useState({});
  const { id } = useParams();
  const { showError } = useError();
  const [openPermissionsPrompt, setOpenPermissionsPrompt] = useState(false);
  const [openAddPermissionsSuccess, setOpenAddPermissionsSuccess] =
    useState(false);

  const togglePermissionsPrompt = () => {
    setOpenPermissionsPrompt(true);
  };
  const closePermissionsPrompt = () => {
    setOpenPermissionsPrompt(false);
  };

  const toggleAddPermissionsSuccess = () => {
    setOpenAddPermissionsSuccess(true);
  };
  const closeAddPermissionsSuccess = () => {
    setOpenAddPermissionsSuccess(false);
  };

  const handleAddPermissions = () => {
    addPermissionMutate();
  };

  const { mutate: addPermissionMutate, isLoading: loadingAddPermissions } =
    useMutation({
      mutationKey: ["add permissions"],
      mutationFn: () => addUserPermissions(id, getSelectedPermissions()),
      onSuccess: () => {
        closePermissionsPrompt();
        toggleAddPermissionsSuccess();
      },
      onError: (error) => {
        showError(error?.response?.data?.message || error.message);
      },
    });

  const handleRefetchUsers = () => {
    refetchModules();
    refetchUser();
  };

  const { data: user, refetch: refetchUser } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
    onSuccess: (data) => {
      const permissionIds = data?.userPermissions?.map((p) => p?.id) || [];
      const normalized = permissionIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      setPermissionsState(normalized);
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const { data: allModules, refetch: refetchModules } = useQuery({
    queryKey: ["modules"],
    queryFn: () => getModules(),
    initialData: [],
  });

  const handleToggle = (permissionId, checked) => {
    setPermissionsState((prev) => ({
      ...prev,
      [permissionId]: checked,
    }));
  };

  const getSelectedPermissions = () => {
    return Object.entries(permissionsState)
      .filter(([_, checked]) => checked)
      .map(([id]) => id);
  };
  return (
    <>
      {openAddPermissionsSuccess && (
        <AddPermissionsSuccess
          closeAddUserPermissionsSuccess={closeAddPermissionsSuccess}
          handleRefetchUsers={handleRefetchUsers}
        />
      )}
      {openPermissionsPrompt && (
        <AddPermissions
          closePromptAddPermissions={closePermissionsPrompt}
          handleAddPermissions={handleAddPermissions}
          loading={loadingAddPermissions}
        />
      )}
      <div className="h-[145px] bg-[#0055A6] w-full py-[20px] px-[10px] flex justify-between">
        <div className="flex gap-[20px]">
          <div className="h-[94px] w-[94px] rounded-[20px] overflow-hidden">
            {user?.profile_image ? (
              <img
                className="h-full w-full object-cover rounded-[20px]"
                src={user?.profile_image}
                alt="user"
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="94"
                height="94"
                viewBox="0 0 24 24"
              >
                <g
                  fill="none"
                  stroke="#ffffff"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                >
                  <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0" />
                  <path d="M9 10a3 3 0 1 0 6 0a3 3 0 1 0-6 0m-2.832 8.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855" />
                </g>
              </svg>
            )}
          </div>
          <div className="h-full flex justify-around flex-col">
            <p className="text-white font-light text-[14px]">
              #{user?.public_id}
            </p>
            <div className="flex items-center gap-[10px]">
              <p className="font-medium capitalize text-[16px] text-white">
                {user?.firstname} {user?.lastname}
              </p>
              <div className="flex items-center gap-[5px]">
                <Circle size={17} color="#00A676" fill="#00A676" />
                <p className="text-[#00A676] text-[14px] font-normal">Active</p>
              </div>
            </div>
            <div className="flex items-start">
              <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                Role
              </p>
              <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                Branch
              </p>
              <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3A6]">
                Phone
              </p>
            </div>
            <div className="flex items-start">
              <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                {user?.role?.name ?? "N/A"}
              </p>
              <p className="font-medium capitalize w-[140px] text-[14px] text-[#F3F3F3]">
                {user?.branch?.name ?? "N/A"}
              </p>
              <p className="font-medium w-[140px] text-[14px] text-[#F3F3F3]">
                {user?.phone ?? "N/A"}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[44px] h-[40px] border border-[#F3F3F3] rounded-[5px] flex items-center justify-center">
            <Ellipsis color="white" />
          </div>
          <a href={`mailto:${user?.email}`}>
            <button className="h-[40px] bg-[#FFFFFF] px-[20px] rounded-[30px] text-[15px] text-[#000000] font-medium flex items-center justify-center gap-[10px]">
              <Mail size={22} color="#000000" />
              Send Email
            </button>
          </a>
        </div>
      </div>
      <div className="w-full pl-[20px]">
        <div class="mb-6 mt-[10px] w-full">
          <h1 class="text-2xl font-bold text-[#222222]">
            Manage User Permissions
          </h1>
          <p class="mt-2 text-sm text-[#5C5C5C] w-full">
            Set and control what actions users are allowed to perform.
            Permissions are grouped by modules such as user management, loan
            processing, and reporting. Use the switches to grant or revoke
            specific privileges. Changes are applied immediately and help
            maintain security, compliance, and accountability across the system.
          </p>
        </div>
        <div>
          {allModules &&
            allModules.map((module) => (
              <div>
                <p
                  key={module.id}
                  className="text-[#000000] mb-[15px] font-bold text-[16px]"
                >
                  {module?.name}
                </p>
                {module?.permissions?.map((permission) => (
                  <div
                    key={permission?.id}
                    className="w-full flex items-center mb-[15px] pl-[10px] justify-between"
                  >
                    <div>
                      <p className="text-[#000000] font-normal text-[14px]">
                        {permission?.name}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="relative h-full w-[25%] flex items-center">
                        <Switch
                          checked={permissionsState[permission.id] || false}
                          onChange={(checked) =>
                            handleToggle(permission.id, checked)
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>
        <div className="w-full my-[20px] flex items-center justify-end">
          <button
            onClick={togglePermissionsPrompt}
            className="h-[35px] bg-[#000000] rounded-[30px] px-[15px] text-[15px] font-bold text-white flex items-center justify-center gap-[5px]"
          >
            Save Changes
          </button>
        </div>
      </div>
    </>
  );
};

export default EditUserPermissions;
